import { useEffect, useRef, useState } from "react";
import { Copy, Mail, Share2, RotateCcw, Flag, PlugZap, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const when = () => new Date().toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });
const subj = (q) => `Vesta · ${(q || "risposta").slice(0, 60)}`;
export const turnText = (t) => `Domanda: ${t.domanda}\n\nRisposta di Vesta (${when()}):\n${t.risposta}`;
export const copyText = async (txt) => {
  try { await navigator.clipboard.writeText(txt); toast.success("Testo copiato"); } catch { toast.error("Copia non riuscita: tocca il pulsante Copia"); }
};
export const turnDraft = (t) => ({ subject: subj(t.domanda), body: turnText(t) });
export const summaryDraft = (turns) => ({ subject: `Vesta · riepilogo del ${when()}`, body: summaryText(turns) });
export const summaryText = (turns) => turns.map((t, i) => `${i + 1}. ${t.domanda}\n${t.risposta}`).join("\n\n");

function Actions({ t, onReplay, onMail }) {
  const share = () => navigator.share({ title: subj(t.domanda), text: turnText(t) }).catch(() => {});
  const flag = () => api.post(`/vesta/voice/segnala/${t.log_id}`).then(() => toast.success("Segnalata: la vedrà l'Ufficio Tecnico")).catch(() => toast.error("Segnalazione non riuscita"));
  const B = ({ icon: I, label, onClick, id }) => (
    <button type="button" onClick={onClick} title={label} data-testid={`vesta-voice-act-${id}`}
      className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] text-neutral-400 transition-colors hover:bg-white/10 hover:text-[#E8B48F]"><I className="h-3.5 w-3.5" />{label}</button>
  );
  return (
    <div className="mt-2 flex flex-wrap justify-center gap-1" data-testid="vesta-voice-actions">
      <B icon={Copy} label="Copia" id="copy" onClick={() => copyText(t.risposta)} />
      {onMail && <B icon={Mail} label="Email" id="mail" onClick={() => onMail(turnDraft(t))} />}
      {navigator.share && <B icon={Share2} label="Condividi" id="share" onClick={share} />}
      {t.audio && <B icon={RotateCcw} label="Riascolta" id="replay" onClick={() => onReplay(t)} />}
      {t.log_id && <B icon={Flag} label="Segnala errore" id="flag" onClick={flag} />}
    </div>
  );
}

// Pulsante azione proposto da Vesta: un tocco esegue davvero il comando sulla colonnina.
function EvAzione({ az, src, onConferma }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const run = async () => {
    if (busy || done) return;
    setBusy(true);
    try { await src.evAzione(az); setDone(true); if (onConferma) onConferma(az); else toast.success("Fatto"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita, riprova"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 flex justify-center" data-testid="vesta-ev-azione">
      <button type="button" onClick={run} disabled={busy || done} data-testid="vesta-ev-azione-btn"
        className="inline-flex items-center gap-2 rounded-full bg-[#C17F59] px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-black shadow-lg transition-transform hover:scale-105 disabled:scale-100 disabled:opacity-70">
        {done ? <Check className="h-4 w-4" /> : <PlugZap className="h-4 w-4" />}
        {done ? "Fatto" : busy ? "Invio…" : az.label}
      </button>
    </div>
  );
}

// Trascrizione della conversazione: l'ultima risposta resta in alto sotto la fiamma,
// lo storico scorre verso il basso (ordine invertito: più recente in cima).
export function Transcript({ turns, progress, speaking, onReplay, onMail, piena, src, onConferma }) {
  const lastTurn = useRef(null);
  // riporta in cima (sotto la fiamma) all'arrivo di ogni nuovo messaggio
  useEffect(() => { lastTurn.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, [turns.length]);
  if (!turns.length) return null;
  // più recente in cima, storico a scendere
  const ordinati = turns.map((t, i) => ({ t, i })).reverse();
  // nella scheda cliente il testo scorre insieme a tutta la scheda (un solo scroll, funziona anche su iPhone)
  return (
    <div className={`mt-6 w-full max-w-xl px-2 text-center ${piena ? "" : "max-h-[38vh] overflow-y-auto"}`} data-testid="vesta-voice-transcript">
      {ordinati.map(({ t, i }) => {
        const cur = i === turns.length - 1;
        const cut = cur && speaking ? Math.round(t.risposta.length * progress) : t.risposta.length;
        return (
          <div key={i} ref={cur ? lastTurn : null} style={cur ? { scrollMarginTop: piena ? 150 : 220 } : undefined} className={`py-3 ${cur ? "" : "opacity-60"}`} data-testid={`vesta-voice-turn-${i}`}>
            {t.domanda && <p className="text-xs text-neutral-500">«{t.domanda}»</p>}
            <p className="mt-1.5 text-sm leading-relaxed" data-testid={cur ? "vesta-voice-answer" : undefined}>
              <span className="text-[#E8B48F]">{t.risposta.slice(0, cut)}</span><span className="text-neutral-400">{t.risposta.slice(cut)}</span>
            </p>
            {(!cur || !speaking) && <Actions t={t} onReplay={onReplay} onMail={onMail} />}
            {(!cur || !speaking) && t.azione && src && <EvAzione az={t.azione} src={src} onConferma={onConferma} />}
          </div>
        );
      })}
    </div>
  );
}

export function SummaryPanel({ turns, onClose, onMail }) {
  return (
    <div className="mt-6 flex flex-col items-center gap-3 text-center" data-testid="vesta-voice-summary">
      <p className="text-sm text-neutral-300">Conversazione di {turns.length} {turns.length === 1 ? "domanda" : "domande"}. Vuoi il riepilogo?</p>
      <div className="flex flex-wrap justify-center gap-2">
        {onMail && <button type="button" onClick={() => onMail(summaryDraft(turns))} data-testid="vesta-voice-summary-mail"
          className="inline-flex items-center gap-2 rounded-full bg-[#C17F59] px-4 py-2 text-xs font-bold uppercase tracking-widest text-black transition-transform hover:scale-105"><Mail className="h-4 w-4" />Invia riepilogo</button>}
        <button type="button" onClick={() => copyText(summaryText(turns))} data-testid="vesta-voice-summary-copy"
          className="inline-flex items-center gap-2 rounded-full border border-neutral-700 px-4 py-2 text-xs font-bold uppercase tracking-widest text-neutral-300 hover:border-[#C17F59]"><Copy className="h-4 w-4" />Copia</button>
        <button type="button" onClick={onClose} data-testid="vesta-voice-summary-close" className="rounded-full px-4 py-2 text-xs font-bold uppercase tracking-widest text-neutral-500 hover:text-white">Chiudi</button>
      </div>
    </div>
  );
}
